"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.movesFunctions = void 0;
const calculationsCore_1 = require("../calculationsCore");
const blackScholes_1 = require("../calculationsCore/blackScholes");
const types_1 = require("../types");
const instrumentUtils_1 = require("./instrumentUtils");
const getFutureSimulatedMoves = (moves, underlyingPrice, amountCurrency) => {
    return moves.map((move) => {
        const simulatedMove = move * underlyingPrice * amountCurrency;
        return [simulatedMove, simulatedMove, simulatedMove];
    });
};
const calculateSimulatedVolatility = (markIV, daysToExpiry, volChange, exponent) => {
    return (1 + volChange * Math.pow((30 / daysToExpiry), exponent)) * markIV;
};
// Volatility
const calculateOptionPnl = (option, ticker, simulatedUnderlyingPrice, simulatedIndexPrice, volatility, calculationTimestamp) => {
    const { optionType, strike } = (0, calculationsCore_1.getOptionInstrumentProperties)(option.instrument);
    const expirationTimestamp = (0, calculationsCore_1.getExpiryTimestampFromInstrumentString)(option.instrument);
    const yearsToExpiry = (0, calculationsCore_1.getTimeInYearsBetweenDates)(calculationTimestamp, expirationTimestamp);
    const bs = (0, blackScholes_1.blackScholes)(optionType, simulatedUnderlyingPrice, strike, yearsToExpiry, 0, volatility / 100);
    return (option.amount *
        (bs / simulatedUnderlyingPrice - ticker.markPrice) *
        simulatedIndexPrice);
};
const calculateLinearOptionPnl = (option, ticker, simulatedUnderlyingPrice, simulatedIndexPrice, volatility, calculationTimestamp) => {
    const { optionType, strike } = (0, calculationsCore_1.getOptionInstrumentProperties)(option.instrument);
    const expirationTimestamp = (0, calculationsCore_1.getExpiryTimestampFromInstrumentString)(option.instrument);
    const yearsToExpiry = (0, calculationsCore_1.getTimeInYearsBetweenDates)(calculationTimestamp, expirationTimestamp);
    const bs = (0, blackScholes_1.blackScholes)(optionType, simulatedUnderlyingPrice, strike, yearsToExpiry, 0, volatility / 100);
    return (option.amount *
        (bs / simulatedUnderlyingPrice - ticker.markPrice) *
        simulatedIndexPrice);
};
const getOptionSimulatedMove = (move, ticker, option, pmParams, calculationTimestamp) => {
    const positionType = (0, instrumentUtils_1.getInstrumentType)(option.instrument);
    const optionPnlCalcFunction = positionType === types_1.PositionType.Option
        ? calculateOptionPnl
        : calculateLinearOptionPnl;
    const expirationTimestamp = (0, calculationsCore_1.getExpiryTimestampFromInstrumentString)(option.instrument);
    const yearsToExpiry = (0, calculationsCore_1.getTimeInYearsBetweenDates)(calculationTimestamp, expirationTimestamp);
    const simulatedUnderlyingPrice = ticker.underlyingPrice * (1 + move);
    const simulatedIndexPrice = ticker.indexPrice * (1 + move);
    const daysToExpiry = 365 * yearsToExpiry;
    const vegaPower = daysToExpiry < 30
        ? pmParams.general.shortTermVegaPower
        : pmParams.general.longTermVegaPower;
    const simulatedIVDown = calculateSimulatedVolatility(ticker.volatility / 100, daysToExpiry, pmParams.general.volRangeDown, vegaPower);
    const simulatedIVUp = calculateSimulatedVolatility(ticker.volatility / 100, daysToExpiry, pmParams.general.volRangeUp, vegaPower);
    const optionPnlDown = optionPnlCalcFunction(option, ticker, simulatedUnderlyingPrice, simulatedIndexPrice, simulatedIVDown * 100, calculationTimestamp);
    const optionPnlSame = optionPnlCalcFunction(option, ticker, simulatedUnderlyingPrice, simulatedIndexPrice, ticker.volatility, calculationTimestamp);
    const optionPnlUp = optionPnlCalcFunction(option, ticker, simulatedUnderlyingPrice, simulatedIndexPrice, simulatedIVUp * 100, calculationTimestamp);
    return [optionPnlDown, optionPnlSame, optionPnlUp];
};
const getOptionSimulatedMoves = (moves, ticker, option, pmParams, calculationTimestamp) => {
    return moves.map((move) => {
        return getOptionSimulatedMove(move, ticker, option, pmParams, calculationTimestamp);
    });
};
/**
 * Functions that calculate pnl for different moves in the market
 */
exports.movesFunctions = {
    [types_1.PositionType.Future]: (position, ticker, pmParams, moves, calculationTimestamp) => {
        return getFutureSimulatedMoves(moves, ticker.markPrice, position.amountCurrency);
    },
    [types_1.PositionType.InversePerpetual]: (position, ticker, pmParams, moves, calculationTimestamp) => {
        return getFutureSimulatedMoves(moves, ticker.markPrice, position.amountCurrency);
    },
    [types_1.PositionType.Option]: (position, ticker, pmParams, moves, calculationTimestamp) => {
        return getOptionSimulatedMoves(moves, ticker, position, pmParams, calculationTimestamp);
    },
    [types_1.PositionType.LinearFuture]: (position, ticker, pmParams, moves, calculationTimestamp) => {
        return getFutureSimulatedMoves(moves, ticker.markPrice, position.amountCurrency);
    },
    [types_1.PositionType.LinearOption]: (position, ticker, pmParams, moves, calculationTimestamp) => {
        return getOptionSimulatedMoves(moves, ticker, position, pmParams, calculationTimestamp);
    },
    [types_1.PositionType.LinearPerpetual]: (position, ticker, pmParams, moves, calculationTimestamp) => {
        return getFutureSimulatedMoves(moves, ticker.markPrice, position.amountCurrency);
    },
    [types_1.PositionType.VolatilityFuture]: (position, ticker, pmParams, moves, calculationTimestamp) => {
        return moves.map((m) => [0, 0, 0]);
    },
};
